import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $isElementNode,
  $isLineBreakNode,
  type EditorState,
  type LexicalEditor,
  TextNode
} from 'lexical';

import { $createMentionNode, $isMentionNode } from '../../ui/data-builder/formula-content-form/formula-editor-lexical/mention-node';
import { DELETED_FORMULA_REFERENCE_CODE, FormulaReferenceOption } from './types';

const FORMULA_REFERENCE_REGEX = /REF\(([^)]+)\)/g;

const getReferenceId = (serializedId: string): string => {
  if (serializedId.startsWith('"') && serializedId.endsWith('"')) {
    return serializedId.slice(1, -1);
  }

  return serializedId;
};

/**
 * Converts the given Lexical EditorState to a string which can be saved to the database.
 * References are converted to the `REF("id")` format that the backend expects.
 * This is the Lexical equivalent of `convertEditorStateToFormulaString` (draft-js version).
 */
export const convertLexicalStateToFormulaString = (editorState: EditorState): string => {
  return editorState.read(() => {
    const paragraphs = $getRoot().getChildren();

    return paragraphs
      .map(paragraph => {
        let line = '';
        if ($isElementNode(paragraph)) {
          paragraph.getChildren().forEach(node => {
            if ($isMentionNode(node)) {
              line += `REF("${node.getMentionId()}")`;
            } else if ($isLineBreakNode(node)) {
              line += '\n';
            } else if (node instanceof TextNode) {
              line += node.getTextContent();
            }
          });
        }
        return line.trim() ? line : '\n';
      })
      .join('\n');
  });
};

/**
 * Populates the given Lexical editor with content built from a formula string coming from the
 * backend, e.g. `IF(REF("v2:static:income:transactional")=2, 2, 3)`. Each `REF("id")` token is
 * resolved against `initialOptions` and rendered as an "@name" mention node, matching the visual
 * behaviour of the draft-js `convertFormulaStringToEditorState` function. References that can't be
 * resolved (e.g. deleted accounts/drivers) are rendered as an "invalid" mention using
 * `DELETED_REFERENCE_CODE`, same as the draft-js `InvalidMention` decorator.
 */
export const populateEditorFromFormulaString = (
  editor: LexicalEditor,
  formulaContent: string,
  initialOptions: FormulaReferenceOption[]
): void => {
  editor.update(() => {
    const root = $getRoot();
    root.clear();

    if (!formulaContent.length) {
      root.append($createParagraphNode());
      return;
    }

    formulaContent.split('\n').forEach(line => {
      const paragraph = $createParagraphNode();
      let cursor = 0;
      let match: RegExpExecArray | null;

      FORMULA_REFERENCE_REGEX.lastIndex = 0;
      while ((match = FORMULA_REFERENCE_REGEX.exec(line)) !== null) {
        if (match.index > cursor) {
          paragraph.append($createTextNode(line.slice(cursor, match.index)));
        }

        const referenceId = getReferenceId(match[1]);
        const option = initialOptions.find(candidate => String(candidate.id) === referenceId);
        paragraph.append(
          $createMentionNode(
            referenceId,
            option?.name ?? DELETED_FORMULA_REFERENCE_CODE,
            option?.invalid ?? !option
          )
        );
        cursor = match.index + match[0].length;
      }

      if (cursor < line.length) {
        paragraph.append($createTextNode(line.slice(cursor)));
      }

      root.append(paragraph);
    });
  }, { discrete: true });
};
